var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
/** A titled card surface that groups a set of related settings. */
export var SettingsSection = function (props) { return (React.createElement("section", { className: (props.inset
        ? "border border-[var(--border-subtle)] bg-[var(--surface-panel-soft)]"
        : "sj-panel") + " mb-4 p-4 sm:p-5", style: props.inset ? { borderRadius: "var(--radius-2xl)" } : undefined },
    React.createElement("header", { className: "mb-3 flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border-subtle)] pb-2" },
        React.createElement("div", null,
            React.createElement("h3", { className: "m-0 text-base font-bold tracking-tight text-[var(--text-primary)]" }, props.title),
            props.subtitle !== undefined ? (React.createElement("p", { className: "m-0 mt-0.5 text-xs text-[var(--text-secondary)]" }, props.subtitle)) : null),
        props.action !== undefined ? React.createElement("div", null, props.action) : null),
    React.createElement("div", { className: "flex flex-col gap-3" }, props.children))); };
/*
 * A single labeled setting: a description on the left, the control on the
 * right. Stacks vertically on narrow screens. Use this to wrap a <select>,
 * <input>, toggle, or button cluster.
 */
export var SettingRow = function (props) { return (React.createElement("div", { className: "flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4" },
    React.createElement("label", { htmlFor: props.htmlFor, className: "flex flex-col text-sm font-medium text-[var(--text-primary)] sm:max-w-[60%]" },
        React.createElement("span", null, props.label),
        props.hint !== undefined ? (React.createElement("span", { className: "mt-0.5 text-xs font-normal text-[var(--text-secondary)]" }, props.hint)) : null),
    React.createElement("div", { className: "flex shrink-0 flex-wrap items-center gap-2 sm:justify-end" }, props.children))); };
/*
 * A themed <select>. Forwards every prop straight through, so callers keep
 * using `value`/`onChange` exactly as before — only the styling changes.
 */
export var SettingSelect = function (props) {
    var className = props.className, children = props.children, rest = __rest(props, ["className", "children"]);
    return (React.createElement("select", __assign({ className: "sj-input !min-h-[40px] w-full !py-1 sm:w-auto sm:min-w-[14rem]" +
            (className !== undefined ? " ".concat(className) : "") }, rest), children));
};
/*
 * A themed <input> (text/number/etc). Same passthrough contract as
 * SettingSelect.
 */
export var SettingInput = function (props) {
    var className = props.className, rest = __rest(props, ["className"]);
    return (React.createElement("input", __assign({ className: "sj-input !min-h-[40px] !py-1" +
            (className !== undefined ? " ".concat(className) : "") }, rest)));
};
/*
 * A themed secondary button — replacement for the legacy `button.normal`
 * styling inside the settings UI.
 */
export var SettingButton = function (props) {
    var className = props.className, children = props.children, rest = __rest(props, ["className", "children"]);
    return (React.createElement("button", __assign({ className: "sj-btn !min-h-[40px] !px-3 !py-1 !text-sm" +
            (className !== undefined ? " ".concat(className) : "") }, rest), children));
};
/*
 * A themed on/off switch backed by a real checkbox input (keeps keyboard +
 * form semantics). Used for boolean settings in the user-preferences pane.
 */
export var SettingToggle = function (props) { return (React.createElement("label", { className: "sj-toggle", title: props["aria-label"] },
    React.createElement("input", { type: "checkbox", name: props.name, checked: props.checked, onChange: props.onChange, "aria-label": props["aria-label"] }),
    React.createElement("span", { className: "sj-toggle-track", "aria-hidden": "true" },
        React.createElement("span", { className: "sj-toggle-thumb" })))); };
/*
 * A labeled boolean setting row: description on the left, a switch on the
 * right. Mirrors SettingRow's responsive layout.
 */
export var SettingToggleRow = function (props) { return (React.createElement("div", { className: "flex items-center justify-between gap-4" },
    React.createElement("span", { className: "flex flex-col text-sm font-medium text-[var(--text-primary)]" },
        React.createElement("span", null, props.label),
        props.hint !== undefined ? (React.createElement("span", { className: "mt-0.5 text-xs font-normal text-[var(--text-secondary)]" }, props.hint)) : null),
    React.createElement(SettingToggle, { checked: props.checked, onChange: props.onChange, name: props.name, "aria-label": typeof props.label === "string" ? props.label : undefined }))); };
